import { Icon } from '@/modules/Icon';
import { cn } from '@/shared/utils/cn';
import { faCircleCheck, faLock } from '@fortawesome/pro-regular-svg-icons';
import type { GhanttBarLabelProps } from '../../types/ghanttBarLabel';
import {
  ghanttBarLabelCardClasses,
  ghanttBarLabelProgressFillClass,
  ghanttBarLabelResolveTheme,
  ghanttBarLabelTextClasses,
  GHANTT_BAR_LABEL_TRANSITION_PROGRESS,
  GHANTT_BAR_LABEL_TRANSITION_TEXT,
} from './ghanttBarLabelStyles';

const DEFAULT_TEXT = {
  fieldLabel: 'Label',
  title: 'String value',
  dateRange: 'Mar 13 - Mar 20',
} as const;

function clampProgress(value: number): number {
  return Math.min(100, Math.max(0, value));
}

/**
 * Etiqueta flotante de barra Gantt (Figma `Gantt Bar`, nodo `143:7108`).
 * Cubre variantes `Type` (timeline / progress), `Stage` y `Theme`.
 *
 * Con `interactive`, el hover es **CSS** (`:hover` en la tarjeta), no `stage="hover"` en JS.
 * El estado Select se controla con `selected` (típicamente tras un clic).
 *
 * @example
 * ```tsx
 * <GhanttBarLabel
 *   interactive
 *   selected={isSelected}
 *   onClick={() => setSelected((v) => !v)}
 *   type="progress"
 *   theme="complete"
 *   text={{ title: 'Diseño UX', dateRange: 'Mar 13 - Mar 20' }}
 *   progress={45}
 * />
 * ```
 */
export function GhanttBarLabel({
  type = 'progress',
  stage = 'default',
  theme = 'default',
  text,
  progress,
  showPercentage,
  interactive = false,
  selected = false,
  onClick,
  className,
}: GhanttBarLabelProps) {
  const mergedText = { ...DEFAULT_TEXT, ...text };
  const themeTokens = ghanttBarLabelResolveTheme(type, theme);
  const hasProgressValue = typeof progress === 'number';
  const showPct = showPercentage ?? hasProgressValue;
  const progressPct = hasProgressValue ? clampProgress(progress) : 0;
  const isDisabled = stage === 'disabled';
  const isLocked = stage === 'locked';
  const isInteractiveCard = interactive && stage === 'default' && !isDisabled && !isLocked;

  const styleOptions = { interactive: isInteractiveCard, selected, type };
  const textClasses = ghanttBarLabelTextClasses(stage, themeTokens, styleOptions);
  const showThemeIcon =
    stage !== 'locked' && stage !== 'disabled' && themeTokens.showStatusIcon && type === 'progress';
  const showLockIcon = stage === 'locked';

  const cardClassName = cn(
    'ui-flex ui-min-h-[47px] ui-w-full ui-flex-col ui-justify-center',
    ghanttBarLabelCardClasses(stage, themeTokens, type, styleOptions)
  );

  const cardInner = (
    <>
      <div className="ui-pointer-events-none ui-absolute ui-inset-y-0 ui-left-0 ui-right-0 ui-flex ui-items-stretch">
        {stage === 'resize' ? (
          <span className="ui-w-2 ui-shrink-0 ui-bg-fill-brand" aria-hidden />
        ) : null}

        <div className="ui-relative ui-min-w-0 ui-flex-1">
          {stage === 'locked' ? (
            <span
              className="ui-absolute ui-inset-0 ui-opacity-30"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(-45deg, transparent, transparent 6px, var(--ui-color-border-default) 6px, var(--ui-color-border-default) 7px)',
              }}
              aria-hidden
            />
          ) : null}

          {type === 'progress' && hasProgressValue ? (
            <span
              className={cn(
                'ui-absolute ui-inset-y-0 ui-left-0',
                GHANTT_BAR_LABEL_TRANSITION_PROGRESS,
                ghanttBarLabelProgressFillClass(stage, themeTokens, type, styleOptions)
              )}
              style={{ width: `${progressPct}%` }}
              aria-hidden
            />
          ) : null}
        </div>

        {stage === 'resize' ? (
          <span className="ui-w-2 ui-shrink-0 ui-bg-fill-brand" aria-hidden />
        ) : null}
      </div>

      <div className="ui-relative ui-z-[1] ui-flex ui-items-center ui-gap-8 ui-px-16 ui-py-4">
        <div className="ui-flex ui-min-w-0 ui-flex-1 ui-items-center ui-gap-8">
          {showLockIcon ? (
            <Icon name={faLock} size="sm" color="secondary" className="ui-shrink-0" />
          ) : null}
          {showThemeIcon ? (
            <Icon
              name={faCircleCheck}
              size="sm"
              color={themeTokens.icon}
              className="ui-shrink-0"
            />
          ) : null}

          <div className="ui-flex ui-min-w-0 ui-flex-1 ui-flex-col ui-justify-center">
            <span
              className={cn(
                'ui-truncate ui-text-body-sm-regular ui-font-body-sm-medium',
                GHANTT_BAR_LABEL_TRANSITION_TEXT,
                textClasses.title
              )}
            >
              {mergedText.title}
            </span>
            {mergedText.dateRange ? (
              <span
                className={cn(
                  'ui-text-body-xs-regular ui-font-body-xs-regular',
                  GHANTT_BAR_LABEL_TRANSITION_TEXT,
                  textClasses.meta
                )}
              >
                {mergedText.dateRange}
              </span>
            ) : null}
          </div>
        </div>

        {showPct && hasProgressValue ? (
          <span
            className={cn(
              'ui-shrink-0 ui-whitespace-nowrap ui-text-body-xs-regular ui-font-body-xs-regular',
              GHANTT_BAR_LABEL_TRANSITION_TEXT,
              textClasses.meta
            )}
          >
            {progressPct}%
          </span>
        ) : null}
      </div>
    </>
  );

  return (
    <div className={cn('ui-flex ui-w-full ui-max-w-[356px] ui-flex-col ui-gap-[5px]', className)}>
      {mergedText.fieldLabel ? (
        <span className="ui-text-label-md-regular ui-font-label-md-regular ui-text-primary">
          {mergedText.fieldLabel}
        </span>
      ) : null}

      {isInteractiveCard ? (
        <button
          type="button"
          className={cardClassName}
          data-selected={selected ? 'true' : 'false'}
          aria-pressed={selected}
          onClick={onClick}
        >
          {cardInner}
        </button>
      ) : (
        <div
          className={cardClassName}
          data-selected={selected ? 'true' : 'false'}
          aria-disabled={isDisabled || isLocked ? true : undefined}
        >
          {cardInner}
        </div>
      )}
    </div>
  );
}

GhanttBarLabel.displayName = 'GhanttBarLabel';
